import React, { useMemo } from 'react';
import styled from 'styled-components';

import Row from 'components/Common/Row';
import Badge from 'kingpin/atoms/Badge';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import useBonusEligibility from '../../hooks/useBonusEligibility';

const ReasonsDiv = styled.div<{ isQualified: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;

  // An override can unlock a driver who still fails requirements, so match the
  // banner's colour rather than always using danger.
  border-top: 1px solid
    ${(props) =>
      props.isQualified
        ? 'var(--color-success-border)'
        : 'var(--color-danger-border)'};
`;

/**
 * Lists the qualifiers / disqualifiers the driver is failing, so the override
 * screen makes it obvious *why* they're disqualified — including any that are
 * already overridden. Shown on boards with `isDisqualificationReasonsEnabled`.
 */
const DisqualificationReasons = ({
  expectations,
  isQualified,
}: {
  expectations: ReturnType<typeof useBonusEligibility>['expectations'];
  isQualified: boolean;
}) => {
  const failing = useMemo(
    () => expectations.filter((e) => !e.isPassed),
    [expectations],
  );

  if (failing.length === 0) {
    return null;
  }

  return (
    <ReasonsDiv
      data-testid={'disqualification-reasons'}
      isQualified={isQualified}
    >
      <Typography.Body type={'Label'} color={'var(--color-text-muted)'}>
        Failing requirements
      </Typography.Body>
      {failing.map((e) => (
        <Row key={e.key} spaceBetween centerAlign style={{ gap: 16 }}>
          <Row centerAlign style={{ gap: 8 }}>
            <Icon icon={'cross'} color={'var(--color-status-danger)'} />
            <Typography.Body type={'Label'}>{e.label}</Typography.Body>
            {e.output.overridden && (
              <Badge badgeType={'Orange'}>
                <Typography.Body type={'Badge'}>Overridden</Typography.Body>
              </Badge>
            )}
          </Row>
          <Typography.Body
            type={'Label'}
          >{`${e.actual} / ${e.target}`}</Typography.Body>
        </Row>
      ))}
    </ReasonsDiv>
  );
};

export default DisqualificationReasons;
